import { set } from 'lodash/fp';
import { ZodSchema } from 'zod';
import { issueMapper } from './issueMapper';

export const validateWithZod =
    <P extends object>(schemaFunction: ZodSchema | ((props: P) => ZodSchema)) =>
    <V extends object>(values: V, props: P = {} as P) => {
        const schema = typeof schemaFunction === 'function' ? schemaFunction(props) : schemaFunction;

        const result = schema.safeParse(values);

        if (result.success) {
            return {};
        }

        return result.error.issues.reduce((acc, issue) => set(issue.path)(issueMapper(issue))(acc), {});
    };

export const validateWithZodAsync =
    <P extends object>(schemaFunction: ZodSchema | ((props: P) => ZodSchema)) =>
    async <V extends object>(values: V, props: P = {} as P) => {
        const schema = typeof schemaFunction === 'function' ? schemaFunction(props) : schemaFunction;

        const result = await schema.safeParseAsync(values);

        if (result.success) {
            return {};
        }

        return result.error.issues.reduce((acc, issue) => set(issue.path)(issueMapper(issue))(acc), {});
    };

/**
 * Experimental function to return transformed values via the schema transformation
 */
export const experimentalValidateWithZod =
    <P extends object>(schemaFunction: ZodSchema | ((props: P) => ZodSchema)) =>
    <V extends object>(values: V, props: P = {} as P) => {
        const schema = typeof schemaFunction === 'function' ? schemaFunction(props) : schemaFunction;

        const result = schema.safeParse(values);

        if (result.success) {
            return {
                errors: {},
                values: result.data,
            };
        }

        return {
            errors: result.error.issues.reduce((acc, issue) => set(issue.path)(issueMapper(issue))(acc), {}),
            values,
        };
    };

export type { ZodSchema };
